import { z } from "zod";
import { AccountCreatedFromSource, accountSchema } from "./account";
import { billingIntervalSchema } from "./billing";
import { TipName } from "./tip";
import { planFeatures } from "./plan-features";
import { Plan } from "../../consts";

export enum Permission {
  editor,
  viewer,
}

export type MondayUser = {
  id: string;
  account_id: string;
  name: string;
  email: string;
  account_name: string;
  is_guest: boolean;
  is_admin: boolean;
  sign_up_product_kind: string;
  account_products: { kind: string }[];
};

export const planSchema = z.object({
  name: z.string(),
  plan_id: z.nativeEnum(Plan).optional(),
  interval: billingIntervalSchema.nullish(),
  invoice_paid: z.boolean(),
  enc_features: z.string().optional(),
  features: z.array(planFeatures).optional(),
  seats_count: z.number(),
  stripe_user_id: z.string(),
  stripe_subscription_id: z.string().nullish(),
  trial_end_date: z.date().nullish(),
  trial_start_date: z.date().nullish(),
  plan_start_date: z.date().nullish(),
  plan_cancelled_date: z.date().nullish(),
  source: z.string().nullish(),
  is_downgraded: z.boolean().default(false).optional(),
  is_active: z.boolean().optional(),
});

export function planInfoFromDBModel(obj: any) {
  if (obj) {
    let trialEndDate,
      trialStartDate,
      planStartDate,
      planCancelledDate: Date | null = null;
    if (obj.trial_end_date) {
      trialEndDate = new Date(obj.trial_end_date);
    }
    if (obj.trial_start_date) {
      trialStartDate = new Date(obj.trial_start_date);
    }
    if (obj.plan_start_date) {
      planStartDate = new Date(obj.plan_start_date);
    }
    if (obj.plan_cancelled_date) {
      planCancelledDate = new Date(obj.plan_cancelled_date);
    }
    return planSchema.parse({
      ...obj,
      trial_end_date: trialEndDate,
      trial_start_date: trialStartDate,
      plan_start_date: planStartDate,
      plan_cancelled_date: planCancelledDate,
    });
  }
  return null;
}

export type PlanInfo = z.infer<typeof planSchema>;

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  thumbnail: z.string(),
  photo: z.string(),
  pending: z.boolean(),
  account: accountSchema.nullish(),
  accountId: z.string().nullish(),
  planInfo: planSchema.nullish(),
  createdAt: z.string().nullish(),
  isNewSignup: z.boolean().nullish(),
  canvasCreatedDate: z.string().nullish(),
  repsToken: z.string().nullish(),
  serialNumber: z.number().optional(),
  tipsSeen: z.array(z.nativeEnum(TipName)).default([]),
  isGuest: z.boolean().default(false),
  isAdmin: z.boolean().default(false),
  isVerified: z.boolean().default(false),
  permission: z.nativeEnum(Permission).default(Permission.editor),
  isSubscribedToCanvas: z.boolean().default(true),
  canInvite: z.boolean().default(false),
  accountSource: z.nativeEnum(AccountCreatedFromSource).nullish(),
});

export const minimalUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  photo_thumb: z.string(),
  photo_original: z.string(),
  is_pending: z.boolean(),
  created_at: z.string().nullish(),
  is_guest: z.boolean().default(false),
  is_admin: z.boolean().default(false),
  permission: z.nativeEnum(Permission).default(Permission.editor),
  isSubscribedToCanvas: z.boolean().default(true),
});

export const invitedUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  token: z.string(),
  expiration: z.string(),
  permission: z.nativeEnum(Permission).default(Permission.editor),
});

export type InvitedUser = z.infer<typeof invitedUserSchema>;

export const invitedUserNewSchema = z.object({
  id: z.string(),
  email: z.string(),
  token: z.string(),
  expiration: z.string(),
  permission: z.nativeEnum(Permission).default(Permission.editor),
});

export type InvitedUserNew = z.infer<typeof invitedUserNewSchema>;

export interface CreatedUser {
  user: { id: number; email: string };
  email: string;
  error: string[];
}

function safeParseTips(tips: string[] | undefined | null) {
  if (!tips) {
    return [];
  }
  const parsed = new Array<TipName>();
  tips.forEach((tipStr) => {
    const tip = z.nativeEnum(TipName).safeParse(tipStr);
    if (tip.success) {
      parsed.push(tip.data);
    }
  });
  return parsed;
}

export const mondayUserRoleSchema = z.object({
  isAdmin: z.boolean().default(false),
  isGuest: z.boolean().default(false),
  isSubscribedToCanvas: z.boolean().default(true),
});

export type MondayUserRole = z.infer<typeof mondayUserRoleSchema>;

export function userFromDBModel(obj: any): User | null {
  if (obj) {
    const planInfo = planInfoFromDBModel(obj.planInfo);
    const canInvite = obj.is_admin || (obj.permission !== Permission.viewer && !obj.is_guest);
    try {
      const user = userSchema.parse({
        id: obj.id.toString(),
        name: obj.name,
        email: obj.email,
        thumbnail: obj.photo_thumb,
        photo: obj.photo_original,
        pending: obj.is_pending,
        account: obj.account,
        accountId: obj.account?.id,
        planInfo,
        createdAt: obj.created_at,
        isNewSignup: obj.isNewSignup,
        canvasCreatedDate: obj.canvasCreatedDate,
        repsToken: obj.repsToken,
        serialNumber: obj.serialNumber,
        tipsSeen: safeParseTips(z.array(z.string()).optional().parse(obj.tipsSeen)),
        isGuest: obj.is_guest ?? false,
        isAdmin: obj.is_admin ?? false,
        isVerified: obj.is_verified ?? false,
        permission: obj.permission ?? Permission.editor,
        isSubscribedToCanvas: obj.isSubscribedToCanvas,
        canInvite,
        accountSource: obj.accountSource,
      });
      return user;
    } catch (error) {
      console.error("Error parsing user from DB model", error);
    }
  }
  return null;
}

export type User = z.infer<typeof userSchema>;

export const usersOnPage = 50;

export interface UsersCache {
  [key: string]: { users: User[]; page: number; hasMore: boolean; failed: boolean }; // key is type-id (e.g. team-1)
}

const userCacheSchema = z.record(
  z.string(), // userId key
  z.object({
    user: userSchema,
    lastUpdated: z.date(),
  })
);

export type UserCache = z.infer<typeof userCacheSchema>;

export const defaultMondayUsersFields = [
  "id",
  "name",
  "email",
  "photo_thumb",
  "photo_original",
  "is_pending",
  "created_at",
  "is_guest",
  "is_view_only",
];
